import { FC, useMemo, useState } from 'react'

import { match } from 'ts-pattern'

import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { usePublishedQuote } from 'Cube/view/layouts/quote/hooks'

import { PublishQuoteModalProps } from '../types'
import { ModalAutoRejected } from '../AutoRejected.modal'
import { ModalLoading } from '../Loading.modal'
import { ModalPublish } from './Publish.modal'
import { ModalRepublish } from './Republish.modal'
import { ModalRepublishReadyToSignUnsigned } from './RepublishAfterReadyToSignUnsigned.modal'
import { ModalRepublishReadyToSignPartiallySigned } from './RepublishAfterReadyToSignPartiallySigned.modal'
import { ModalWithEsignatureProps } from './types'
import { ModalRepublishAfterExpired } from './RepublishAfterExpired.modal'
import { ModalRepublishAfterExpiredReadyToSignNoChanges } from './RepublishAfterExpiredReadyToSignNoChanges.modal'
import { ModalRepublishAfterExpiredReadyToSignWithChanges } from './RepublishAfterExpiredReadyToSignWithChanges.modal'
import { usePublishRecipients } from '../usePublishRecipients'

type ModalVariant =
  | 'LOADING'
  | 'AUTO_REJECTED'
  | 'PUBLISH'
  | 'REPUBLISH'
  | 'REPUBLISH_AFTER_READY_TO_SIGN_UNSIGNED'
  | 'REPUBLISH_AFTER_READY_TO_SIGN_PARTIALLY_SIGNED'
  | 'REPUBLISH_AFTER_EXPIRED'
  | 'REPUBLISH_AFTER_EXPIRED_READY_TO_SIGN_NO_CHANGES'
  | 'REPUBLISH_AFTER_EXPIRED_READY_TO_SIGN_WITH_CHANGES'

export const ModalWithEsignatures: FC<PublishQuoteModalProps> = ({
  onClose,
  onSubmit,
  isQuoteSyncing,
  submitting,
  isApprovalRequired,
  isSimulationPending,
  isAutoRejected
}) => {
  const ctx = useCubeContext()

  const recipients = usePublishRecipients({ isApprovalRequired })

  /**
   * A save the modal started itself - someone moving a recipient between signer
   * and cc - must not swap the list for a spinner, or they lose their place
   * mid-change. Every other save still holds the modal, because which variant
   * they get is worked out from the saved quote.
   */
  const isWaitingForSync = isQuoteSyncing && !recipients.hasEditedTypes

  const [message, setMessage] = useState<string>('')
  const [approvalComment, setApprovalComment] = useState<string>('')

  const isReadyToSign = !!ctx.queries.rawData.data.quote.readyToSignAt
  const isExpired = ctx.queries.rawData.data.quote.isExpired

  const { data: publishedQuote, isLoading: isPublishedQuoteLoading } =
    usePublishedQuote()
  const allSigners = [
    ...(publishedQuote?.contacts ?? []),
    ...(publishedQuote?.counterSigners ?? [])
  ]
  const signedSigners = allSigners.filter(c => !!c.signedAt)

  const modalVariant: ModalVariant = useMemo(() => {
    // The prediction decides which of these the user sees, so we wait for it
    // rather than showing one and swapping it under them.
    if (isPublishedQuoteLoading || isWaitingForSync || isSimulationPending) {
      return 'LOADING'
    }

    if (isAutoRejected) {
      return 'AUTO_REJECTED'
    }

    const hasChanges =
      ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible

    if (isExpired) {
      if (!isReadyToSign) {
        // Expired + user edits since expiry falls through to plain REPUBLISH by design.
        return hasChanges ? 'REPUBLISH' : 'REPUBLISH_AFTER_EXPIRED'
      }
      return hasChanges
        ? 'REPUBLISH_AFTER_EXPIRED_READY_TO_SIGN_WITH_CHANGES'
        : 'REPUBLISH_AFTER_EXPIRED_READY_TO_SIGN_NO_CHANGES'
    }

    if (isReadyToSign && hasChanges) {
      return signedSigners.length > 0
        ? 'REPUBLISH_AFTER_READY_TO_SIGN_PARTIALLY_SIGNED'
        : 'REPUBLISH_AFTER_READY_TO_SIGN_UNSIGNED'
    }

    return hasChanges ? 'REPUBLISH' : 'PUBLISH'
  }, [
    isAutoRejected,
    isReadyToSign,
    isExpired,
    isWaitingForSync,
    isSimulationPending,
    isPublishedQuoteLoading,
    signedSigners.length,
    ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible
  ])

  const modalProps: ModalWithEsignatureProps = {
    onClose,
    onSubmit,
    submitting,
    isApprovalRequired,
    message,
    setMessage,
    approvalComment,
    setApprovalComment,
    recipients
  }

  return match(modalVariant)
    .with('LOADING', () => <ModalLoading />)
    .with('AUTO_REJECTED', () => <ModalAutoRejected onClose={onClose} />)
    .with('PUBLISH', () => <ModalPublish {...modalProps} />)
    .with('REPUBLISH', () => <ModalRepublish {...modalProps} />)
    .with('REPUBLISH_AFTER_READY_TO_SIGN_UNSIGNED', () => (
      <ModalRepublishReadyToSignUnsigned {...modalProps} />
    ))
    .with('REPUBLISH_AFTER_READY_TO_SIGN_PARTIALLY_SIGNED', () => (
      <ModalRepublishReadyToSignPartiallySigned
        {...modalProps}
        allSignersCount={allSigners.length}
        signedSignersCount={signedSigners.length}
      />
    ))
    .with('REPUBLISH_AFTER_EXPIRED', () => (
      <ModalRepublishAfterExpired {...modalProps} />
    ))
    .with('REPUBLISH_AFTER_EXPIRED_READY_TO_SIGN_NO_CHANGES', () => (
      <ModalRepublishAfterExpiredReadyToSignNoChanges {...modalProps} />
    ))
    .with('REPUBLISH_AFTER_EXPIRED_READY_TO_SIGN_WITH_CHANGES', () => (
      <ModalRepublishAfterExpiredReadyToSignWithChanges {...modalProps} />
    ))
    .exhaustive()
}
